import {
  Backend,
  getQueryParamValue,
  OrderByDirection,
} from '@freelancer/datastore/core';
import { isDefined } from '@freelancer/utils';
import { TextEntityCreateObjectApi } from 'api-typings/common/common';
import { Comment, ExtraCommentContext } from './comments.model';
import { CommentsCollection } from './comments.types';

export function commentsBackend(): Backend<CommentsCollection> {
  return {
    defaultOrder: {
      field: 'timeCreated',
      direction: OrderByDirection.DESC,
    },
    fetch: (authUid, ids, query, order) => ({
      endpoint: `messages/0.1/comments/`,
      isGaf: false,
      params: {
        comment_ids: ids,
        comment_feed_ids: getQueryParamValue(query, 'threadId'),
      },
    }),
    push: (authUid, comment: Comment, extraContext?: ExtraCommentContext) => ({
      endpoint: `messages/0.1/comments/`,
      payload: {
        comment: {
          fromUser: comment.fromUser,
          message: comment.comment,
          thread_id: comment.threadId,
          message_source: comment.commentSource,
        },
        text_entities: extraContext?.textEntities
          ?.filter(isDefined)
          .map(textEntity => ({
            reference_type: textEntity.referenceType,
            reference_id: textEntity.referenceId,
            offset: textEntity.offset,
            length: textEntity.length,
            content: textEntity.content,
          })),
      },
    }),
    set: undefined,
    update: (authUid, delta, original) => {
      if (!original.id) {
        throw new Error('Missing comment id.');
      }

      if (
        delta.textEntities &&
        delta.textEntities.some(
          textEntity =>
            textEntity === undefined ||
            textEntity?.referenceType === undefined ||
            textEntity?.offset === undefined ||
            textEntity?.length === undefined,
        )
      ) {
        throw new Error(
          'Missing required fields on Text Entity: referenceType, offset, length',
        );
      }

      const textEntities:
        | ReadonlyArray<TextEntityCreateObjectApi>
        | undefined = delta.textEntities
        ?.map(textEntity =>
          textEntity &&
          textEntity.referenceType &&
          isDefined(textEntity.offset) &&
          isDefined(textEntity.length)
            ? {
                reference_type: textEntity.referenceType,
                reference_id: textEntity.referenceId,
                offset: textEntity.offset,
                length: textEntity.length,
                content: textEntity.content,
              }
            : undefined,
        )
        .filter(isDefined);

      return {
        endpoint: `messages/0.1/comments/${original.id}`,
        method: 'PUT',
        payload: {
          comment_id: original.id,
          comment: delta.comment,
          text_entities: textEntities,
        },
      };
    },
    remove: undefined,
  };
}
